import { Vector3 } from '@babylonjs/core';
import { NPCController, NPCAIBehavior } from '../systems/NPCController';
import { SplineConstraint } from '../systems/SplineConstraint';
import { logger } from '../core/Logger';

/**
 * Patrol state for SplinePatrolAI state machine
 */
type PatrolState = 'idle' | 'walkingRight' | 'walkingLeft' | 'waiting';

/**
 * Configuration for SplinePatrolAI
 */
export interface SplinePatrolConfig {
  /** Max distance to patrol left from spawn point (meters) */
  leftDistance: number;
  /** Max distance to patrol right from spawn point (meters) */
  rightDistance: number;
  /** Pause duration at patrol endpoints (seconds) */
  waitTime: number;
  /** Speed multiplier (default 1.0) */
  speed?: number;
  /** Enable cliff detection (default true) */
  cliffDetection?: boolean;
  /** Minimum Y drop to consider a cliff (meters, default 1.5) */
  cliffThreshold?: number;
  /** Distance ahead to check for cliffs (meters, default 0.5) */
  cliffCheckDistance?: number;
}

/**
 * AI that patrols left/right on the gameplay spline within distance constraints from spawn point.
 * Features:
 * - Configurable patrol distances (leftDistance, rightDistance or radius shorthand)
 * - Cliff detection to prevent walking off edges
 * - Wait timer at patrol endpoints before reversing
 * - Uses existing gameplay spline (no separate patrol path needed)
 */
export class SplinePatrolAI implements NPCAIBehavior {
  private state: PatrolState = 'idle';
  private direction: number = 1; // 1 = right (positive T), -1 = left (negative T)
  private waitTimer: number = 0;
  
  // Patrol bounds in T values
  private minT: number;
  private maxT: number;
  private spawnT: number;
  
  // Configuration
  private readonly waitTime: number;
  private readonly speed: number;
  private readonly cliffDetection: boolean;
  private readonly cliffThreshold: number;
  private readonly cliffCheckDistance: number;
  
  // Spline reference for cliff checks
  private splineConstraint: SplineConstraint;
  
  /**
   * Create a SplinePatrolAI
   * @param spawnT The T value on spline where NPC spawned
   * @param config Patrol configuration
   * @param splineConstraint Reference to spline for calculations
   */
  constructor(
    spawnT: number,
    config: SplinePatrolConfig,
    splineConstraint: SplineConstraint
  ) {
    this.spawnT = spawnT;
    this.splineConstraint = splineConstraint;
    
    // Config with defaults
    this.waitTime = config.waitTime;
    this.speed = config.speed ?? 1.0;
    this.cliffDetection = config.cliffDetection ?? true;
    this.cliffThreshold = config.cliffThreshold ?? 1.5;
    this.cliffCheckDistance = config.cliffCheckDistance ?? 0.5;
    
    // Convert distances to T values
    const splineLength = splineConstraint.getTotalLength();
    const leftT = config.leftDistance / splineLength;
    const rightT = config.rightDistance / splineLength;
    
    // Calculate min/max T from spawn point
    this.minT = Math.max(0, spawnT - leftT);
    this.maxT = Math.min(1, spawnT + rightT);
    
    // Ensure valid range
    if (this.minT >= this.maxT) {
      logger.npc.warn(`SplinePatrolAI: Invalid patrol range [${this.minT}, ${this.maxT}], defaulting to ±0.1`);
      this.minT = Math.max(0, spawnT - 0.1);
      this.maxT = Math.min(1, spawnT + 0.1);
    }
    
    // Start walking right
    this.state = 'walkingRight';
    this.direction = 1;
    
    logger.npc.debug(`SplinePatrolAI created: spawnT=${spawnT.toFixed(3)}, range=[${this.minT.toFixed(3)}, ${this.maxT.toFixed(3)}], leftDist=${config.leftDistance}m, rightDist=${config.rightDistance}m`);
  }
  
  /**
   * Check if there's a cliff ahead in the current movement direction
   */
  private checkForCliff(npc: NPCController): boolean {
    if (!this.cliffDetection) return false;
    
    // Get current position
    const currentPos = npc.getPosition();
    const currentY = currentPos.y;
    
    // Project a point ahead along the spline
    const splineLength = this.splineConstraint.getTotalLength();
    const checkT = npc.playerT + (this.direction * this.cliffCheckDistance / splineLength);
    
    // Clamp to valid T range
    const clampedT = Math.max(0, Math.min(1, checkT));
    
    // Get Y position at that point on spline
    const aheadPos = this.splineConstraint.getPointAt(clampedT);
    if (!aheadPos) return false;
    
    const aheadY = aheadPos.y;
    
    // Check for sudden Y drop
    const yDrop = currentY - aheadY;
    if (yDrop > this.cliffThreshold) {
      logger.npc.debug(`SplinePatrolAI: Cliff detected! currentY=${currentY.toFixed(2)}, aheadY=${aheadY.toFixed(2)}, drop=${yDrop.toFixed(2)}m`);
      return true;
    }
    
    return false;
  }
  
  update(deltaTime: number, npc: NPCController): void {
    // Set camera vectors to point along the spline
    const splineForward = new Vector3(0, 0, 1);
    const splineRight = new Vector3(1, 0, 0);
    npc.setCameraVectors(splineForward, splineRight);
    
    const currentT = npc.playerT;
    
    // Debug: log state every frame for first few seconds
    logger.npc.debug(`SplinePatrolAI: state=${this.state}, currentT=${currentT.toFixed(3)}, range=[${this.minT.toFixed(3)}, ${this.maxT.toFixed(3)}]`);
    
    // State machine
    switch (this.state) {
      case 'idle':
        // Not used in normal operation, but safe fallback
        npc.setMovementInput(0);
        this.state = 'walkingRight';
        break;
        
      case 'walkingRight':
        // Check if reached right boundary
        if (currentT >= this.maxT) {
          this.startWaiting();
          break;
        }
        
        // Check for cliff
        if (this.checkForCliff(npc)) {
          this.startWaiting();
          break;
        }
        
        // Continue walking right
        npc.setMovementInput(this.speed);
        break;
        
      case 'walkingLeft':
        // Check if reached left boundary
        if (currentT <= this.minT) {
          this.startWaiting();
          break;
        }
        
        // Check for cliff
        if (this.checkForCliff(npc)) {
          this.startWaiting();
          break;
        }
        
        // Continue walking left
        npc.setMovementInput(-this.speed);
        break;
        
      case 'waiting':
        npc.setMovementInput(0);
        this.waitTimer += deltaTime;
        
        if (this.waitTimer >= this.waitTime) {
          // Finished waiting, switch direction
          this.direction *= -1;
          this.waitTimer = 0;
          
          if (this.direction > 0) {
            this.state = 'walkingRight';
          } else {
            this.state = 'walkingLeft';
          }
          
          logger.npc.debug(`SplinePatrolAI: Finished waiting, now ${this.state}`);
        }
        break;
    }
    
    // Never jump or attack
    npc.setJumpInput(false);
    npc.setAttackInput(false);
  }
  
  private startWaiting(): void {
    this.state = 'waiting';
    this.waitTimer = 0;
    logger.npc.debug(`SplinePatrolAI: Starting wait at T=${this.splineConstraint ? 'unknown' : 'N/A'}`);
  }
  
  /**
   * Get current patrol state for debugging/state panel
   */
  getState(): PatrolState {
    return this.state;
  }
  
  /**
   * Get patrol bounds for debugging
   */
  getBounds(): { minT: number; maxT: number; spawnT: number } {
    return { minT: this.minT, maxT: this.maxT, spawnT: this.spawnT };
  }
}
